
import { NoseurObject, Subscriber } from "@rivtn/noseur";
import { ApiResponseData, BaseService, IAddress, IAnalyticsData, IEarningSource, IGallery, INotification, IWebsitePage, SanitizedResponse, TransactionFlow, TransactionSourceType, Visibility } from "../BaseService";
import { NotificationGroup, Notifier } from "../../components/shared/Notifiier";
import { IMember } from "./MemberService";
import { IProgram, ProgramService } from "./ProgramService";
import { ITournamentEdition, TournamentService, TournamentTestService } from "./TournamentService";
import { Utils } from "../../utils";
import { i18nManager } from "../../i18n";

export enum TeamRole {

    OWNER = "OWNER",
    MEMBER = "MEMBER",
    MODERATOR = "MODERATOR",

}

export enum TeamMemberStatus {

    ACTIVE = "ACTIVE",
    KICKED = "KICKED",

}

export enum TeamReceivingGroup {

    OWNER = "OWNER",
    SPECIFIC_MODERATOR = "SPECIFIC_MODERATOR",
    SELECTED_TEAM_MEMBERS = "SELECTED_TEAM_MEMBERS",
    SPLIT_EQUALLY_BETWEEN_MODERATORS = "SPLIT_EQUALLY_BETWEEN_MODERATORS",
    SPLIT_EQUALLY_BETWEEN_TEAM_MEMBERS = "SPLIT_EQUALLY_BETWEEN_TEAM_MEMBERS",

}

export enum TeamEarningReceiver {

    OWNER = "OWNER",
    MODERATOR = "MODERATOR",
    SELECTED_TEAM_MEMBERS = "SELECTED_TEAM_MEMBERS",
    SPLITTED_EQUALLY_BETWEEN_MODERATORS = "SPLITTED_EQUALLY_BETWEEN_MODERATORS",
    SPLITTED_EQUALLY_BETWEEN_TEAM_MEMBERS = "SPLITTED_EQUALLY_BETWEEN_TEAM_MEMBERS",

}

export enum TeamTournamentStatus {

    WON = "WON",
    LOST = "LOST",
    ENTERED = "ENTERED",
    SKIPPED = "SKIPPED",
    PARTICIPATING = "PARTICIPATING",

}

export enum TeamAchievementType {

    PLACE = "PLACE",
    TROPHY = "TROPHY",
    OTHERS = "OTHERS",

}

export enum TeamAchievementPlace {

    FIRST_PLACE = "FIRST_PLACE",
    THIRD_PLACE = "THIRD_PLACE",
    SECOND_PLACE = "SECOND_PLACE",
    CUSTOM_PLACE = "CUSTOM_PLACE",

}

export interface ITeam {
    name: string;
    rule?: string;
    status: string;
    role: TeamRole;
    logo_url: string;
    homepage: string;
    public_id: string;
    passcode?: string;
    created_at: string;
    description: string;
    external_id: string;
    country_code: string;
    is_moderator: boolean;
    visibility: Visibility;
    qr_code_base64: string;
    member_external_id: string;
    extra_data?: NoseurObject<any>;
    un_reads: {
        messages: number;
        notifications: number;
    };
    earning_settings: {
        address?: IAddress;
        accepted_terms: boolean;
        receiving_member_ids: string[];
        receiving_group: TeamReceivingGroup;
    };
}

export interface ITeamInvite {
    team: ITeam;
    join_code: string;
    expiry_date: string;
}

export interface ITeamMember {
    team: ITeam;
    role: TeamRole;
    extra_data: any;
    join_date?: string;
    exit_date?: string;
    created_at: string;
    last_joined: string;
    external_id: string;
    display_name: string;
    display_role?: string;
    profile_details: string;
    status: TeamMemberStatus;
    member?: Partial<IMember>;
    passport_picture_url: string;
    half_body_picture_url: string;
    social_accounts: NoseurObject<string>;
    settings: {
        country: string;
        state_region: string;
        show_link_to_profile: boolean;
        hide_from_members_page: boolean;
        show_recent_achievements: boolean;
    };
}

export interface ITeamWebsite {
    created_by: string;
    updated_by: string;
    sponsors: string[];
    created_at: string;
    updated_at: string;
    external_id: string;
    extra_data: NoseurObject<any>;
    social_accounts: NoseurObject<any>;
    media: {
        LOGO: string[];
        BANNER: string[];
    };
    website_pages: {
        HOME: IWebsitePage;
        NEWS: IWebsitePage;
        MERCH: IWebsitePage;
        MEMBERS: IWebsitePage;
        GALLERY: IWebsitePage;
        SCHEDULE: IWebsitePage;
        ACTIVITIES: IWebsitePage;
        ACHIEVEMENTS: IWebsitePage;
    }
    news_settings: {
        enable_rivtn_ad: boolean;
    };
    gallery_settings: {
        enable_rivtn_ad: boolean;
    };
    members_settings: {
        enable_rivtn_ad: boolean;
        show_previous_team_members: boolean;
    };
    merchandise_settings: {
        storefront_id: string;
        enable_rivtn_ad: boolean;
    };
    design_settings: {
        design_id: string;
        scheme_settings: NoseurObject<any>;
    };
    schedule_settings: {
        enable_rivtn_ad: boolean;
        show_past_matches: boolean;
        show_ongoing_upcoming_matches: boolean;
        show_past_team_members_matches: boolean;
        show_ongoing_upcoming_team_members_matches: boolean;
    };
    home_settings: {
        show_sponsors: boolean;
        show_schedules: boolean;
        enable_rivtn_ad: boolean;
        show_top_gallery: boolean;
        show_twitter_feeds: boolean;
        show_top_activities: boolean;
        show_twitch_streams: boolean;
        show_instagram_feeds: boolean;
    };
    activities_settings: {
        show_trophies: boolean;
        enable_rivtn_ad: boolean;
        show_timeline_graph: boolean;
        show_members_trails: boolean;
        show_members_graphs: boolean;
        show_timeline_trails: boolean;
        show_activities_graph: boolean;
        show_tournament_trails: boolean;
        show_tournament_graphs: boolean;
        show_activities_trails: boolean;
        show_achievements_trails: boolean;
        show_achievements_graphs: boolean;
    };
}

export interface ITeamAnalytics {
    earnings?: {
        total?: IAnalyticsData;
        tournament?: IAnalyticsData;
        merchandise?: IAnalyticsData;
        advertisement?: IAnalyticsData;
    };
    tournaments?: {
        won?: IAnalyticsData;
        lost?: IAnalyticsData;
        skipped?: IAnalyticsData;
        entered?: IAnalyticsData;
        participate?: IAnalyticsData;
    };
    achievements?: {
        total?: IAnalyticsData;
        trophies?: IAnalyticsData;
        first_place?: IAnalyticsData;
        second_place?: IAnalyticsData;
        third_place?: IAnalyticsData;
    };
}

export interface ITeamMemberEarning {
    amount: number;
    currency: string;
    team_member: ITeamMember;
}

export interface ITeamTopProgram {
    count: number;
    program: IProgram;
}

export interface ITeamTransaction {
    date: string;
    amount: number;
    external_id: string;
    flow: TransactionFlow;
    source: IEarningSource;
    extra_data?: NoseurObject<any>;
    recipient: ITeamMember | TeamEarningReceiver;
}

export interface ITeamTournament {
    tournamentEdition: ITournamentEdition;
    registration_date: string;
    participants: ITeamMember[];
    status: TeamTournamentStatus;
}

export interface ITeamAchievementDetail {
    title: string;
    receiver?: ITeamMember;
    type: TeamAchievementType;
    place?: TeamAchievementPlace;
    extra_data?: NoseurObject<any>;
}

export interface ITeamAchievement {
    external_id: string;
    date_achieved: string;
    tournamentEdition: ITournamentEdition;
    badge_image_location: string;
    achievement: ITeamAchievementDetail;
}

export interface ITeamNews {
    title: string;
    content: string;
    external_id: string;
    extra_data: NoseurObject<any>;

    featured_image?: string;
}

export class TeamService extends BaseService {

    protected static instance: TeamService;

    private constructor() {
        super();
    }

    static getInstance() {
        if (!TeamService.instance) TeamService.instance = new TeamService();
        return TeamService.instance;
    }

    queryTeams(params: NoseurObject<string | number> = { page: 1, size: 10 }): Promise<any> {
        return this.report(this.transport.get("/v1/team", { query: this.normalizeParams(params) }), ({ sanitized }: SanitizedResponse<ApiResponseData<ITeam>>) => {
            const counts = sanitized.content?.reduce((acc, c) => {
                if (c.role === "OWNER") acc.created++;
                else acc.joined++;
                return acc;
            }, { joined: 0, created: 0 });
            (sanitized as any).team_join_count = counts?.joined;
            (sanitized as any).team_creation_count = counts?.created;
        });
    }

    getTeamByPublicId(id: string): Promise<any> {
        return this.report(this.transport.get(`/v1/team/public/${id}`));
    }

    createTeam(team: Partial<ITeam>): Promise<any> {
        return this.report(this.transport.post("/v1/team", team));
    }

    updateTeam(id: string, team: Partial<ITeam>): Promise<any> {
        return this.report(this.transport.patch(`/v1/team/${id}`, team));
    }

    leaveOrDisbandTeam(id: string): Promise<any> {
        return this.report(this.transport.delete(`/v1/team/${id}`));
    }

    reactivateTeam(id: string): Promise<any> {
        return this.report(this.transport.patch(`/v1/team/${id}/activate`, {}));
    }

    updateTeamEarnings(id: string, payload: any): Promise<any> {
        return this.report(this.transport.put(`/v1/team/${id}/earnings`, payload));
    }

    // notifications

    queryNotifications(params: NoseurObject<string | number | undefined> = { page: 1, size: 10 }): Promise<SanitizedResponse<ApiResponseData<INotification> & { grouped_notifications: NotificationGroup }>> {
        return this.report(this.transport.get("/v1/team/notification", { query: this.normalizeParams(params) }), ({ sanitized }: SanitizedResponse<ApiResponseData<INotification> & { grouped_notifications: NotificationGroup }>) => {
            sanitized.grouped_notifications = Notifier.groupNotifications(sanitized.content!);
        });
    }

    markAllAsRead(teamId: string): Promise<SanitizedResponse<void>> {
        return this.report(this.transport.put(`/v1/team/notification/${teamId}/read_all`, {}));
    }

    updateNotificationStatus(id: string, status = "READ"): Promise<SanitizedResponse<void>> {
        return this.report(this.transport.patch(`/v1/team/notification/${id}`, { status }));
    }

    // end notifications

    // invite

    getSingleTeamInvite(id: string): Promise<SanitizedResponse<ITeamInvite>> {
        return this.report(this.transport.get(`/v1/team/invite/${id}`));
    }

    createOrUpdateJoinCode(teamId: string, joinCode?: string, expiryDate?: string): Promise<SanitizedResponse<ITeamInvite>> {
        return this.report(this.transport.post(`/v1/team/invite/code`, {
            team_id: teamId,
            join_code: joinCode,
            expiry_date: expiryDate,
        }));
    }

    inviteTeamMember(payload: any): Promise<SanitizedResponse<ITeamInvite>> {
        return this.report(this.transport.post(`/v1/team/invite/member`, payload));
    }

    cancelTeamInvite(id: string): Promise<SanitizedResponse<ITeamInvite>> {
        return this.report(this.transport.delete(`/v1/team/invite/${id}`));
    }

    acceptOrRejectTeamInvite(id: string, accept: boolean): Promise<SanitizedResponse<ITeamInvite>> {
        return this.report(this.transport.post(`/v1/team/invite/join`, {
            team_invite_id: id,
            accept_invite: accept,
        }));
    }

    joinTeamWithQrCode(payload: any): Promise<SanitizedResponse<ITeamMember>> {
        return this.report(this.transport.post(`/v1/team/invite/join/qrcode`, payload));
    }

    joinTeamWithJoinCode(payload: any): Promise<SanitizedResponse<ITeamMember>> {
        return this.report(this.transport.post(`/v1/team/invite/join/code`, payload));
    }

    joinTeamWithIdAndPasscode(payload: any): Promise<SanitizedResponse<ITeamMember>> {
        return this.report(this.transport.post(`/v1/team/invite/join/id-and-passcode`, payload));
    }

    // end invite

    // team member

    queryTeamMembers(teamId: string, params: NoseurObject<any> = { page: 1, size: 10 }): Promise<SanitizedResponse<ITeamMember>> {
        params["team.external_id"] = teamId; // TODO FIX IN KYOFUUC
        return this.report(this.transport.get("/v1/team/member", { query: this.normalizeParams(params) }));
    }

    addModerator(id: string): Promise<SanitizedResponse<ITeamMember>> {
        return this.report(this.transport.put(`/v1/team/member/${id}/moderator`, {}));
    }

    transferOwnership(id: string): Promise<SanitizedResponse<ITeamMember>> {
        return this.report(this.transport.put(`/v1/team/member/${id}/ownership`, {}));
    }

    removeModerator(id: string): Promise<SanitizedResponse<ITeamMember>> {
        return this.report(this.transport.delete(`/v1/team/member/${id}/moderator`));
    }

    acceptTeamRules(id: string, payload: any): Promise<SanitizedResponse<ITeamMember>> {
        return this.report(this.transport.put(`/v1/team/member/${id}/accept-rules`, payload));
    }

    kickTeamMember(id: string): Promise<SanitizedResponse<ITeamMember>> {
        return this.report(this.transport.delete(`/v1/team/member/${id}`));
    }

    updateTeamMember(teamMember: ITeamMember): Promise<SanitizedResponse<ITeamMember>> {
        return this.report(this.transport.patch(`/v1/team/member/${teamMember.external_id}`, teamMember));
    }

    // end team member

    // analytics

    getTeamAnalytics(teamId: string, params: NoseurObject<any> = { page: 1, size: 10 }): Promise<SanitizedResponse<ITeamAnalytics>> {
        return this.mockResolve(1000, {
            data: {
                succuss: true,
                message: "Success",
                data: (params.currency_code === "KES"
                    ? {
                        earnings: {
                            total: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                            tournament: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                            merchandise: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                            advertisement: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                        },
                        tournaments: {
                            won: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                            lost: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                            skipped: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                            entered: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                            participate: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                        },
                        achievements: {
                            total: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                            trophies: {
                                count: 0,
                                value: 0,
                                percentage: 0.0,
                            },
                            first_place: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                            second_place: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                            third_place: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                        }
                    } as ITeamAnalytics
                    : {
                        earnings: {
                            total: {
                                count: 30,
                                value: 55600000,
                                percentage: 19.83,
                            },
                            tournament: {
                                count: 10,
                                value: 20454000,
                                percentage: -10.4,
                            },
                            merchandise: {
                                count: 6,
                                value: 20000000,
                                percentage: 98.5,
                            },
                            advertisement: {
                                count: 4,
                                value: 30000000,
                                percentage: 0.0,
                            },
                        },
                        tournaments: {
                            won: {
                                count: 10,
                                value: 10,
                                percentage: 5.38,
                            },
                            lost: {
                                count: 21,
                                value: 21,
                                percentage: -89.32,
                            },
                            skipped: {
                                count: 1,
                                value: 1,
                                percentage: 3.21,
                            },
                            entered: {
                                count: 40,
                                value: 40,
                                percentage: 0.0,
                            },
                            participate: {
                                count: 9,
                                value: 9,
                                percentage: -24.5,
                            },
                        },
                        achievements: {
                            total: {
                                count: 48,
                                value: 48,
                                percentage: -57.91,
                            },
                            trophies: {
                                count: 12,
                                value: 12,
                                percentage: 0.0,
                            },
                            first_place: {
                                count: 9,
                                value: 9,
                                percentage: -4.5,
                            },
                            second_place: {
                                count: 1,
                                value: 1,
                                percentage: 7.5,
                            },
                            third_place: {
                                count: 31,
                                value: 31,
                                percentage: 49.74,
                            },
                        }
                    } as ITeamAnalytics),
            }
        });
        return this.report(this.transport.get(`/v1/team/${teamId}/analytics`, { query: this.normalizeParams(params) }));
    }

    getTeamMemberEarnings(teamId: string, params: NoseurObject<any> = { page: 1, size: 10 }): Promise<SanitizedResponse<ITeamMemberEarning>> {
        return this.mockResolve(1000, {
            data: {
                succuss: true,
                message: "Success",
                data: {
                    size: 10,
                    total_pages: 1,
                    number_of_elements: TeamService.TEST_TEAM_MEMBERS_EARNINGS.length,
                    content: TeamService.TEST_TEAM_MEMBERS_EARNINGS,
                },
            }
        });
        return this.report(this.transport.get(`/v1/team/member/${teamId}/earnings`, { query: this.normalizeParams(params) }));
    }

    getTeamTopPrograms(teamId: string, params: NoseurObject<any> = { page: 1, size: 10 }): Promise<SanitizedResponse<ITeamTopProgram>> {
        return this.mockResolve(1000, {
            data: {
                succuss: true,
                message: "Success",
                data: (params.currency_code === "GBP"
                    ? {
                        size: 10,
                        total_pages: 1,
                        number_of_elements: 0,
                        content: [],
                    }
                    : {
                        size: 10,
                        total_pages: 1,
                        number_of_elements: TeamService.TEST_TEAM_TOP_PROGRAMS.length,
                        content: TeamService.TEST_TEAM_TOP_PROGRAMS,
                    }),
            }
        });
        return this.report(this.transport.get(`/v1/team/member/${teamId}/earnings`, { query: this.normalizeParams(params) }));
    }

    queryAnalyticsChartData(teamId: string, params: NoseurObject<any> = { page: 1, size: 10 }): Promise<any> {
        return this.mockResolve(1000, {
            data: {
                succuss: true,
                message: "Success",
                data: {
                    labels: Array(7).fill(0).map((_, i) => i + 1),
                    datasets: [
                        {
                            label: "earnings",
                            data: Array(7).fill(0).map((_, i) => Utils.randomInt(50000, 500000))
                        },
                        {
                            label: "tournaments",
                            data: Array(7).fill(0).map((_, i) => Utils.randomInt(20, 30000))
                        },
                        {
                            label: "achievements",
                            data: Array(7).fill(0).map((_, i) => Utils.randomInt(0, 20000))
                        },
                    ]
                }
            }
        }, ({ apiResponse }: SanitizedResponse<any>) => {
            apiResponse.data!.datasets = apiResponse.data!.datasets.map((d: any) => ({
                data: d.data,
                label: (i18nManager.Labels.teams.analytics.chart_labels as any)[d.label] ?? d.label,
            }))
        });
        return this.report(this.transport.get(`/v1/team/${teamId}/analytics`, { query: this.normalizeParams(params) }));
    }

    queryTransactions(teamId: string, params: NoseurObject<string | number | any[]> = { page: 1, size: 10 }): Promise<any> {
        return this.mockResolve(1000, {
            data: {
                succuss: true,
                message: "Success",
                data: (params.currency_code == "USD"
                    ? {
                        size: 10,
                        content: [],
                        total_pages: 1,
                        number_of_elements: 0,
                    }
                    : {
                        size: 10,
                        total_pages: 1,
                        content: (params.size ? TeamService.TEST_TEAM_TRANSACTIONS.slice(0, params.size as number) : TeamService.TEST_TEAM_TRANSACTIONS),
                        number_of_elements: params.size ?? TeamService.TEST_TEAM_TRANSACTIONS.length,
                    })
            }
        });
        return this.report(this.transport.get("/v1/transaction", { query: this.normalizeParams(params) }));
    }

    queryTeamTournaments(teamId: string, params: NoseurObject<string | number | any[]> = { page: 1, size: 10 }): Promise<any> {
        return this.mockResolve(1000, {
            data: {
                succuss: true,
                message: "Success",
                data: (params.currency_code == "USD"
                    ? {
                        size: 10,
                        content: [],
                        total_pages: 1,
                        number_of_elements: 0,
                    }
                    : {
                        size: 10,
                        total_pages: 1,
                        content: (params.size ? TeamService.TEST_TEAM_TOURNAMENTS.slice(0, params.size as number) : TeamService.TEST_TEAM_TOURNAMENTS),
                        number_of_elements: params.size ?? TeamService.TEST_TEAM_TOURNAMENTS.length,
                    })
            }
        });
        return this.report(this.transport.get("/v1/transaction", { query: this.normalizeParams(params) }));
    }

    queryTeamAchievements(teamId: string, params: NoseurObject<string | number | any[]> = { page: 1, size: 10 }): Promise<any> {
        return this.mockResolve(1000, {
            data: {
                succuss: true,
                message: "Success",
                data: (params.currency_code == "USD"
                    ? {
                        size: 10,
                        content: [],
                        total_pages: 1,
                        number_of_elements: 0,
                    }
                    : {
                        size: 10,
                        total_pages: 1,
                        content: (params.size ? TeamService.TEST_TEAM_ACHIEVEMENTS.slice(0, params.size as number) : TeamService.TEST_TEAM_ACHIEVEMENTS),
                        number_of_elements: params.size ?? TeamService.TEST_TEAM_ACHIEVEMENTS.length,
                    })
            }
        });
        return this.report(this.transport.get("/v1/transaction", { query: this.normalizeParams(params) }));
    }


    // end analytics

    // settings

    getTeamWebsite(teamId: string): Promise<SanitizedResponse<ITeamWebsite>> {
        return this.report(this.transport.get(`/v1/team/${teamId}/website`));
    }

    updateTeamWebsite(teamId: string, teamWebsite: ITeamWebsite): Promise<SanitizedResponse<ITeamWebsite>> {
        return this.report(this.transport.patch(`/v1/team/${teamId}/website`, teamWebsite));
    }

    getGallery(teamId: string, params: NoseurObject<any> = { page: 1, size: 100 }): Promise<SanitizedResponse<IGallery>> {
        return this.report(this.transport.get(`/v1/team/${teamId}/website/gallery`, { query: this.normalizeParams(params) }));
    }

    createGallery(teamId: string, gallery: Partial<IGallery>): Promise<any> {
        return this.report(this.transport.post(`/v1/team/${teamId}/website/gallery`, gallery));
    }

    updateGallery(teamId: string, gallery: Partial<IGallery>): Promise<any> {
        return this.report(this.transport.patch(`/v1/team/${teamId}/website/gallery/${gallery.external_id}`, gallery));
    }

    deleteGallery(teamId: string, gallery: Partial<IGallery>): Promise<any> {
        return this.report(this.transport.delete(`/v1/team/${teamId}/website/gallery/${gallery.external_id}`));
    }

    queryTeamNews(teamId: string, params: NoseurObject<any> = { page: 1, size: 10 }): Promise<SanitizedResponse<ITeamNews>> {
        params["team.external_id"] = teamId; // TODO FIX IN KYOFUUC
        return this.report(this.transport.get(`/v1/team/${teamId}/website/news`, { query: this.normalizeParams(params) }));
    }

    createTeamNews(teamId: string, article: Partial<ITeamNews>): Promise<any> {
        return this.report(this.transport.post(`/v1/team/${teamId}/website/news`, article));
    }

    updateTeamNews(teamId: string, article: Partial<ITeamNews>): Promise<any> {
        return this.report(this.transport.patch(`/v1/team/${teamId}/website/news/${article.external_id}`, article));
    }

    deleteTeamNews(teamId: string, article: Partial<ITeamNews>): Promise<any> {
        return this.report(this.transport.delete(`/v1/team/${teamId}/website/news/${article.external_id}`));
    }

    // end settings

    static TEST_TEAMS: ITeam[] = [
        {
            external_id: "150e560d-b7d3-4b01-bf67-05f44429f11f",
            created_at: "2025-04-26T17:29:38.000+00:00",
            logo_url: "http://192.168.1.95:9305/api/v1/storage/o/user/omnehost/team/media/logo/150e560d-b7d3-4b01-bf67-05f44429f11f.png",
            name: "Joiner1",
            public_id: "OTD73BD3FDDBF6F54D756F",
            homepage: "https://test.com",
            description: "Desc",
            country_code: "NGA",
            passcode: "testsrtghghhhghg",
            status: "ACTIVE",
            visibility: Visibility.PUBLIC,
            qr_code_base64: "",
            extra_data: {},
            role: TeamRole.OWNER,
            is_moderator: true,
            rule: "WASSUP\n - yahoo",
            member_external_id: "5b5a3030-c59c-406b-9e72-e975cf1d97e6",
            earning_settings: {
                address: undefined,
                accepted_terms: false,
                receiving_group: TeamReceivingGroup.OWNER,
                receiving_member_ids: [
                    "5b5a3030-c59c-406b-9e72-e975cf1d97e6"
                ],
            },
            un_reads: {
                messages: 0,
                notifications: 5
            }
        }
    ];

    static TEST_TEAM_MEMBERS: any[] = [
        {
            extra_data: {},
            external_id: "1",
            team: {} as any,
            role: TeamRole.MODERATOR,
            display_name: "vazrak",
            display_role: "Destroyer",
            profile_details: "Never mess the tyha panda on C",
            status: TeamMemberStatus.ACTIVE,
            created_at: "2025-04-26T17:29:38.000+00:00",
            last_joined: "2025-04-26T17:29:38.000+00:00",
            member: { user_detail: { email: "user1@gmail.com" } as any, profile_banner_url: "https://img.lovepik.com/background/20211021/large/lovepik-colourful-banner-background-image_500374982.jpg" },
            passport_picture_url: "https://images.ctfassets.net/h6goo9gw1hh6/2sNZtFAWOdP1lmQ33VwRN3/24e953b920a9cd0ff2e1d587742a2472/1-intro-photo-final.jpg?w=1200&h=992&fl=progressive&q=70&fm=jpg",
        },
        {
            extra_data: {},
            external_id: "2",
            team: {} as any,
            role: TeamRole.OWNER,
            display_name: "amsiraceht",
            profile_details: "We will we will rock you",
            display_role: "The Terminator",
            status: TeamMemberStatus.ACTIVE,
            created_at: "2025-04-26T17:29:38.000+00:00",
            last_joined: "2025-04-26T17:29:38.000+00:00",
            member: { user_detail: { email: "user2@gmail.com" } as any, profile_banner_url: "https://t4.ftcdn.net/jpg/06/37/22/37/360_F_637223769_KxKjp502bI35Na0dQcQOUp4qCYzdRNIP.jpg", external_id: "5b5a3030-c59c-406b-9e72-e975cf1d97e6" },
            passport_picture_url: "https://images.pexels.com/photos/14653174/pexels-photo-14653174.jpeg",
        },
        {
            extra_data: {},
            external_id: "3",
            team: {} as any,
            role: TeamRole.MEMBER,
            display_role: "Medic",
            display_name: "lukdreaper",
            profile_details: "This is it",
            status: TeamMemberStatus.ACTIVE,
            created_at: "2025-04-26T17:29:38.000+00:00",
            last_joined: "2025-04-26T17:29:38.000+00:00",
            member: { user_detail: { email: "user3@gmail.com" } as any, profile_banner_url: "https://t4.ftcdn.net/jpg/06/11/31/69/360_F_611316958_SMSOp3ML81AAxjJsnr7xdMjJQdBTpy8H.jpg" },
            passport_picture_url: "https://wallpapers.com/images/featured/cute-aesthetic-profile-pictures-pjfl391j3q0f7rlz.jpg",
        },
        {
            extra_data: {},
            external_id: "4",
            team: {} as any,
            role: TeamRole.MEMBER,
            display_role: "Savior",
            display_name: "eccentricity",
            profile_details: "",
            status: TeamMemberStatus.ACTIVE,
            created_at: "2025-04-26T17:29:38.000+00:00",
            last_joined: "2025-04-26T17:29:38.000+00:00",
            member: { user_detail: { email: "user4@gmail.com" } as any, profile_banner_url: "https://penji.co/wp-content/uploads/2024/09/10_Web_Banner_Examples.jpg.webp" },
            passport_picture_url: "https://www.shutterstock.com/image-photo/man-portrait-smile-outdoor-selfie-260nw-2490416235.jpg",
        },
        {
            extra_data: {},
            external_id: "5",
            team: {} as any,
            role: TeamRole.MEMBER,
            display_name: "YungDon",
            profile_details: "",
            status: TeamMemberStatus.KICKED,
            exit_date: "2025-05-16T17:29:38.000+00:00",
            created_at: "2025-04-26T17:29:38.000+00:00",
            last_joined: "2025-04-26T17:29:38.000+00:00",
            member: { user_detail: { email: "user5@gmail.com" } as any, profile_banner_url: "https://images.squarespace-cdn.com/content/v1/62e3bd1d14b7b32e64a6f856/f595fab2-6020-4e1f-acd6-b11818f35cf4/top+banner+design+tips+.png" },
            passport_picture_url: "https://thumbs.dreamstime.com/b/handsome-attractive-male-adult-person-model-winter-autumn-season-head-shots-face-up-close-expression-portraits-vivid-colors-134123931.jpg",
        },
        {
            extra_data: {},
            external_id: "6",
            team: {} as any,
            display_role: "N00b",
            role: TeamRole.MODERATOR,
            display_name: "mubamight",
            profile_details: "",
            status: TeamMemberStatus.ACTIVE,
            created_at: "2025-04-26T17:29:38.000+00:00",
            last_joined: "2025-04-26T17:29:38.000+00:00",
            member: { user_detail: { email: "user6@gmail.com" } as any, profile_banner_url: "https://img.freepik.com/free-vector/christmas-banner-with-stars-design_1048-17557.jpg" },
            passport_picture_url: "https://wallpapers.com/images/hd/cool-profile-picture-87h46gcobjl5e4xu.jpg",
        },
    ];

    static TEST_TEAM_MEMBERS_EARNINGS: ITeamMemberEarning[] = [
        {
            currency: "NGN",
            amount: 5909000,
            team_member: TeamService.TEST_TEAM_MEMBERS[0],
        },
        {
            currency: "NGN",
            amount: 4900020,
            team_member: TeamService.TEST_TEAM_MEMBERS[1],
        },
        {
            currency: "NGN",
            amount: 3090232,
            team_member: TeamService.TEST_TEAM_MEMBERS[2],
        },
        {
            currency: "NGN",
            amount: 2090390,
            team_member: TeamService.TEST_TEAM_MEMBERS[3],
        },
        {
            currency: "NGN",
            amount: 100000,
            team_member: TeamService.TEST_TEAM_MEMBERS[4],
        },
        {
            currency: "NGN",
            amount: 0,
            team_member: TeamService.TEST_TEAM_MEMBERS[5],
        },
    ];

    static TEST_TEAM_TOP_PROGRAMS: ITeamTopProgram[] = [
        {
            count: 56,
            program: ProgramService.TEST_PROGRAMS[14],
        },
        {
            count: 45,
            program: ProgramService.TEST_PROGRAMS[15],
        },
        {
            count: 30,
            program: ProgramService.TEST_PROGRAMS[16],
        },
        {
            count: 29,
            program: ProgramService.TEST_PROGRAMS[17],
        },
        {
            count: 26,
            program: ProgramService.TEST_PROGRAMS[18],
        },
        {
            count: 20,
            program: ProgramService.TEST_PROGRAMS[19],
        },
        {
            count: 16,
            program: ProgramService.TEST_PROGRAMS[20],
        },
        {
            count: 16,
            program: ProgramService.TEST_PROGRAMS[21],
        },
        {
            count: 9,
            program: ProgramService.TEST_PROGRAMS[13],
        },
        {
            count: 1,
            program: ProgramService.TEST_PROGRAMS[12],
        },
    ];

    static TEST_TEAM_TRANSACTIONS: any[] = [
        {
            amount: 9000023,
            external_id: "1",
            flow: TransactionFlow.IN,
            recipient: TeamEarningReceiver.OWNER,
            date: "2025-04-01T19:37:18.000+00:00",
            extra_data: {
                receivers_team_member_ids: [
                    "1"
                ]
            },
            source: {
                id: "1",
                label: "Tournament (Brawl All Out)",
                type: TransactionSourceType.TOURNAMENT,
            },
        },
        {
            amount: 9000023,
            external_id: "2",
            flow: TransactionFlow.OUT,
            date: "2025-04-01T19:37:18.000+00:00",
            recipient: TeamService.TEST_TEAM_MEMBERS[0],
            extra_data: {

            },
            source: {
                id: "1",
                label: "Tournament (Brawl All Out)",
                type: TransactionSourceType.TOURNAMENT,
            },
        },
        {
            amount: 5000000,
            external_id: "3",
            flow: TransactionFlow.IN,
            date: "2025-04-01T19:37:18.000+00:00",
            recipient: TeamEarningReceiver.SPLITTED_EQUALLY_BETWEEN_MODERATORS,
            extra_data: {
                receivers_team_member_ids: [
                    "2",
                    "6",
                ]
            },
            source: {
                id: "1",
                label: "Merch Sales (Team Logo + Shirt)",
                type: TransactionSourceType.MERCHANDISE,
            },
        },
        {
            amount: 2500000,
            external_id: "4",
            flow: TransactionFlow.OUT,
            date: "2025-04-01T19:37:18.000+00:00",
            recipient: TeamService.TEST_TEAM_MEMBERS[1],
            extra_data: {

            },
            source: {
                id: "1",
                label: "Merch Sales (Team Logo + Shirt)",
                type: TransactionSourceType.MERCHANDISE,
            },
        },
        {
            amount: 2500000,
            external_id: "5",
            flow: TransactionFlow.OUT,
            date: "2025-04-01T19:37:18.000+00:00",
            recipient: TeamService.TEST_TEAM_MEMBERS[5],
            extra_data: {

            },
            source: {
                id: "1",
                label: "Merch Sales (Team Logo + Shirt)",
                type: TransactionSourceType.MERCHANDISE,
            },
        },
        {
            amount: 70123,
            external_id: "6",
            flow: TransactionFlow.IN,
            date: "2025-04-01T19:37:18.000+00:00",
            recipient: TeamEarningReceiver.MODERATOR,
            extra_data: {
                receivers_team_member_ids: [
                    "2",
                ]
            },
            source: {
                id: "1",
                label: "Tournament (Chicken Dinnerers)",
                type: TransactionSourceType.TOURNAMENT,
            },
        },
        {
            amount: 70123,
            external_id: "7",
            flow: TransactionFlow.OUT,
            date: "2025-04-01T19:37:18.000+00:00",
            recipient: TeamService.TEST_TEAM_MEMBERS[1],
            extra_data: {

            },
            source: {
                id: "1",
                label: "Tournament (Chicken Dinnerers)",
                type: TransactionSourceType.TOURNAMENT,
            },
        },
        {
            amount: 10000000,
            external_id: "8",
            flow: TransactionFlow.IN,
            date: "2025-04-01T19:37:18.000+00:00",
            recipient: TeamEarningReceiver.SPLITTED_EQUALLY_BETWEEN_TEAM_MEMBERS,
            extra_data: {
                receivers_team_member_ids: [
                    "1",
                    "2",
                    "3",
                    "4",
                    "5",
                    "6",
                ]
            },
            source: {
                id: "1",
                label: "Rivtn Advertisement",
                type: TransactionSourceType.ADVERTISEMENT,
            },
        },
        {
            amount: 1666666,
            external_id: "9",
            flow: TransactionFlow.OUT,
            date: "2025-04-01T19:37:18.000+00:00",
            recipient: TeamService.TEST_TEAM_MEMBERS[0],
            extra_data: {

            },
            source: {
                id: "1",
                label: "Rivtn Advertisement",
                type: TransactionSourceType.ADVERTISEMENT,
            },
        },
        {
            amount: 1666666,
            external_id: "10",
            flow: TransactionFlow.OUT,
            date: "2025-04-01T19:37:18.000+00:00",
            recipient: TeamService.TEST_TEAM_MEMBERS[1],
            extra_data: {

            },
            source: {
                id: "1",
                label: "Rivtn Advertisement",
                type: TransactionSourceType.ADVERTISEMENT,
            },
        },
        {
            amount: 1666666,
            external_id: "11",
            flow: TransactionFlow.OUT,
            date: "2025-04-01T19:37:18.000+00:00",
            recipient: TeamService.TEST_TEAM_MEMBERS[2],
            extra_data: {

            },
            source: {
                id: "1",
                label: "Rivtn Advertisement",
                type: TransactionSourceType.ADVERTISEMENT,
            },
        },
        {
            amount: 1666666,
            external_id: "12",
            flow: TransactionFlow.OUT,
            date: "2025-04-01T19:37:18.000+00:00",
            recipient: TeamService.TEST_TEAM_MEMBERS[3],
            extra_data: {

            },
            source: {
                id: "1",
                label: "Rivtn Advertisement",
                type: TransactionSourceType.ADVERTISEMENT,
            },
        },
        {
            amount: 1666666,
            external_id: "13",
            flow: TransactionFlow.OUT,
            date: "2025-04-01T19:37:18.000+00:00",
            recipient: TeamService.TEST_TEAM_MEMBERS[4],
            extra_data: {

            },
            source: {
                id: "1",
                label: "Rivtn Advertisement",
                type: TransactionSourceType.ADVERTISEMENT,
            },
        },
        {
            amount: 1666666,
            external_id: "14",
            flow: TransactionFlow.OUT,
            date: "2025-04-01T19:37:18.000+00:00",
            recipient: TeamService.TEST_TEAM_MEMBERS[5],
            extra_data: {

            },
            source: {
                id: "1",
                label: "Rivtn Advertisement",
                type: TransactionSourceType.ADVERTISEMENT,
            },
        },
    ];

    static TEST_TEAM_TOURNAMENTS: any[] = [
        {
            status: TeamTournamentStatus.ENTERED,
            tournament: TournamentTestService.TEST_TOURNAMENTS[3],
            registration_date: "2025-01-03T12:08:39.000+00:00",
            participants: [
                TeamService.TEST_TEAM_MEMBERS[0],
                TeamService.TEST_TEAM_MEMBERS[1],
                TeamService.TEST_TEAM_MEMBERS[3],
            ]
        },
        {
            status: TeamTournamentStatus.WON,
            tournament: TournamentTestService.TEST_TOURNAMENTS[4],
            registration_date: "2025-02-01T12:08:39.000+00:00",
            participants: [
                TeamService.TEST_TEAM_MEMBERS[0],
                TeamService.TEST_TEAM_MEMBERS[4],
            ]
        },
        {
            status: TeamTournamentStatus.PARTICIPATING,
            tournament: TournamentTestService.TEST_TOURNAMENTS[5],
            registration_date: "2025-03-01T12:08:39.000+00:00",
            participants: [
                TeamService.TEST_TEAM_MEMBERS[0],
                TeamService.TEST_TEAM_MEMBERS[1],
                TeamService.TEST_TEAM_MEMBERS[2],
                TeamService.TEST_TEAM_MEMBERS[3],
                TeamService.TEST_TEAM_MEMBERS[4],
                TeamService.TEST_TEAM_MEMBERS[5],
            ]
        },
        {
            status: TeamTournamentStatus.LOST,
            tournament: TournamentTestService.TEST_TOURNAMENTS[6],
            registration_date: "2025-04-01T12:08:39.000+00:00",
            participants: [
                TeamService.TEST_TEAM_MEMBERS[0],
                TeamService.TEST_TEAM_MEMBERS[1],
                TeamService.TEST_TEAM_MEMBERS[2],
                TeamService.TEST_TEAM_MEMBERS[5],
            ]
        },
        {
            status: TeamTournamentStatus.WON,
            tournament: TournamentTestService.TEST_TOURNAMENTS[6],
            registration_date: "2025-05-01T12:08:39.000+00:00",
            participants: [
                TeamService.TEST_TEAM_MEMBERS[0],
                TeamService.TEST_TEAM_MEMBERS[1],
                TeamService.TEST_TEAM_MEMBERS[5],
            ]
        },
        {
            status: TeamTournamentStatus.SKIPPED,
            tournament: TournamentTestService.TEST_TOURNAMENTS[7],
            registration_date: "2025-06-01T12:08:39.000+00:00",
            participants: [
                TeamService.TEST_TEAM_MEMBERS[0],
            ]
        },
        {
            status: TeamTournamentStatus.PARTICIPATING,
            tournament: TournamentTestService.TEST_TOURNAMENTS[8],
            registration_date: "2025-06-01T12:08:39.000+00:00",
            participants: [
                TeamService.TEST_TEAM_MEMBERS[0],
                TeamService.TEST_TEAM_MEMBERS[3],
                TeamService.TEST_TEAM_MEMBERS[4],
                TeamService.TEST_TEAM_MEMBERS[5],
            ]
        },
        {
            status: TeamTournamentStatus.SKIPPED,
            tournament: TournamentTestService.TEST_TOURNAMENTS[7],
            registration_date: "2025-07-01T12:08:39.000+00:00",
            participants: [
                TeamService.TEST_TEAM_MEMBERS[5],
            ]
        },
        {
            status: TeamTournamentStatus.SKIPPED,
            tournament: TournamentTestService.TEST_TOURNAMENTS[7],
            registration_date: "2025-06-01T12:08:39.000+00:00",
            participants: [
                TeamService.TEST_TEAM_MEMBERS[0],
            ]
        },
        {
            status: TeamTournamentStatus.PARTICIPATING,
            tournament: TournamentTestService.TEST_TOURNAMENTS[8],
            registration_date: "2025-06-01T12:08:39.000+00:00",
            participants: [
                TeamService.TEST_TEAM_MEMBERS[0],
                TeamService.TEST_TEAM_MEMBERS[3],
                TeamService.TEST_TEAM_MEMBERS[4],
                TeamService.TEST_TEAM_MEMBERS[5],
            ]
        },
        {
            status: TeamTournamentStatus.SKIPPED,
            tournament: TournamentTestService.TEST_TOURNAMENTS[7],
            registration_date: "2025-07-01T12:08:39.000+00:00",
            participants: [
                TeamService.TEST_TEAM_MEMBERS[5],
            ]
        },
        {
            status: TeamTournamentStatus.WON,
            tournament: TournamentTestService.TEST_TOURNAMENTS[4],
            registration_date: "2025-02-01T12:08:39.000+00:00",
            participants: [
                TeamService.TEST_TEAM_MEMBERS[0],
                TeamService.TEST_TEAM_MEMBERS[4],
            ]
        },
        {
            status: TeamTournamentStatus.WON,
            tournament: TournamentTestService.TEST_TOURNAMENTS[6],
            registration_date: "2025-05-01T12:08:39.000+00:00",
            participants: [
                TeamService.TEST_TEAM_MEMBERS[0],
                TeamService.TEST_TEAM_MEMBERS[1],
                TeamService.TEST_TEAM_MEMBERS[5],
            ]
        },
    ];

    static TEST_TEAM_ACHIEVEMENTS: any[] = [
        {
            external_id: "1",
            date_achieved: "2025-01-03T12:08:39.000+00:00",
            tournament: TournamentTestService.TEST_TOURNAMENTS[3],
            badge_image_location: "https://gallery.yopriceville.com/var/resizes/Free-Clipart-Pictures/Badges-and-Labels-PNG/Gold_Badge_Template_Clipart_Picture.png?m=1629829925",
            achievement: {
                title: "Eradicator",
                type: TeamAchievementType.PLACE,
                place: TeamAchievementPlace.FIRST_PLACE,
            },
        },
        {
            external_id: "2",
            date_achieved: "2025-02-03T12:08:39.000+00:00",
            tournament: TournamentTestService.TEST_TOURNAMENTS[4],
            badge_image_location: "https://i.pinimg.com/736x/07/78/56/07785646b7d848b9d01c73dba0fef73c.jpg",
            achievement: {
                title: "Best Shooter",
                type: TeamAchievementType.TROPHY,
                receiver: TeamService.TEST_TEAM_MEMBERS[1],
            },
        },
        {
            external_id: "3",
            date_achieved: "2025-03-03T12:08:39.000+00:00",
            tournament: TournamentTestService.TEST_TOURNAMENTS[5],
            badge_image_location: "https://img.lovepik.com/element_origin_pic/18/10/19/b3e0e2cc73238aef91be4711e5a78508.png_wh300.png",
            achievement: {
                title: "Chappellers",
                type: TeamAchievementType.PLACE,
                place: TeamAchievementPlace.SECOND_PLACE,
            },
        },
        {
            external_id: "4",
            date_achieved: "2025-04-03T12:08:39.000+00:00",
            tournament: TournamentTestService.TEST_TOURNAMENTS[6],
            badge_image_location: "https://png.pngtree.com/element_pic/17/09/02/edd30932d1c6075b4c0e302476b03e13.png",
            achievement: {
                title: "Best Shooter",
                type: TeamAchievementType.OTHERS,
            },
        },
        {
            external_id: "5",
            date_achieved: "2025-05-03T12:08:39.000+00:00",
            tournament: TournamentTestService.TEST_TOURNAMENTS[7],
            badge_image_location: "https://gallery.yopriceville.com/var/resizes/Free-Clipart-Pictures/Badges-and-Labels-PNG/Gold_Badge_Template_Clipart_Picture.png?m=1629829925",
            achievement: {
                title: "Eradicator",
                type: TeamAchievementType.PLACE,
                place: TeamAchievementPlace.FIRST_PLACE,
            },
        },
        {
            external_id: "6",
            date_achieved: "2025-06-03T12:08:39.000+00:00",
            tournament: TournamentTestService.TEST_TOURNAMENTS[8],
            badge_image_location: "https://i.pinimg.com/736x/07/78/56/07785646b7d848b9d01c73dba0fef73c.jpg",
            achievement: {
                title: "Best Shooter",
                type: TeamAchievementType.TROPHY,
                receiver: TeamService.TEST_TEAM_MEMBERS[1],
            },
        },
        {
            external_id: "7",
            date_achieved: "2025-07-03T12:08:39.000+00:00",
            tournament: TournamentTestService.TEST_TOURNAMENTS[9],
            badge_image_location: "https://image.similarpng.com/file/similarpng/very-thumbnail/2020/10/Golden-empty-badge-label-element-design-on-transparent-background-PNG.png",
            achievement: {
                title: "Chappellers",
                type: TeamAchievementType.PLACE,
                place: TeamAchievementPlace.THIRD_PLACE,
            },
        },
        {
            external_id: "8",
            date_achieved: "2025-08-03T12:08:39.000+00:00",
            tournament: TournamentTestService.TEST_TOURNAMENTS[10],
            badge_image_location: "https://png.pngtree.com/element_pic/17/09/02/edd30932d1c6075b4c0e302476b03e13.png",
            achievement: {
                title: "Best Shooter",
                type: TeamAchievementType.OTHERS,
            },
        },
        {
            external_id: "9",
            date_achieved: "2025-03-03T12:08:39.000+00:00",
            tournament: TournamentTestService.TEST_TOURNAMENTS[5],
            badge_image_location: "https://img.lovepik.com/element_origin_pic/18/10/19/b3e0e2cc73238aef91be4711e5a78508.png_wh300.png",
            achievement: {
                title: "Chappellers",
                type: TeamAchievementType.PLACE,
                place: TeamAchievementPlace.SECOND_PLACE,
            },
        },
        {
            external_id: "10",
            date_achieved: "2025-04-03T12:08:39.000+00:00",
            tournament: TournamentTestService.TEST_TOURNAMENTS[6],
            badge_image_location: "https://png.pngtree.com/element_pic/17/09/02/edd30932d1c6075b4c0e302476b03e13.png",
            achievement: {
                title: "Best Shooter",
                type: TeamAchievementType.OTHERS,
            },
        },
        {
            external_id: "11",
            date_achieved: "2025-05-03T12:08:39.000+00:00",
            tournament: TournamentTestService.TEST_TOURNAMENTS[7],
            badge_image_location: "https://gallery.yopriceville.com/var/resizes/Free-Clipart-Pictures/Badges-and-Labels-PNG/Gold_Badge_Template_Clipart_Picture.png?m=1629829925",
            achievement: {
                title: "Eradicator",
                type: TeamAchievementType.PLACE,
                place: TeamAchievementPlace.CUSTOM_PLACE,
                extra_data: {
                    place_value: "7th Place"
                }
            },
        },
        {
            external_id: "12",
            date_achieved: "2025-06-03T12:08:39.000+00:00",
            tournament: TournamentTestService.TEST_TOURNAMENTS[8],
            badge_image_location: "https://i.pinimg.com/736x/07/78/56/07785646b7d848b9d01c73dba0fef73c.jpg",
            achievement: {
                title: "Best Shooter",
                type: TeamAchievementType.TROPHY,
                receiver: TeamService.TEST_TEAM_MEMBERS[1],
            },
        },
        {
            external_id: "13",
            date_achieved: "2025-07-03T12:08:39.000+00:00",
            tournament: TournamentTestService.TEST_TOURNAMENTS[9],
            badge_image_location: "https://image.similarpng.com/file/similarpng/very-thumbnail/2020/10/Golden-empty-badge-label-element-design-on-transparent-background-PNG.png",
            achievement: {
                title: "Chappellers",
                type: TeamAchievementType.PLACE,
                place: TeamAchievementPlace.THIRD_PLACE,
            },
        },
        {
            external_id: "14",
            date_achieved: "2025-08-03T12:08:39.000+00:00",
            tournament: TournamentTestService.TEST_TOURNAMENTS[10],
            badge_image_location: "https://png.pngtree.com/element_pic/17/09/02/edd30932d1c6075b4c0e302476b03e13.png",
            achievement: {
                title: "Best Shooter",
                type: TeamAchievementType.OTHERS,
            },
        },
    ];

    static TEST_TEAM_GALLERY: Partial<IGallery>[] = [
        {
            external_id: "1",
            caption: "wassup",
            action_label: "Dezz Nuts",
            action_location: "https://rivtn.com",
            media_location: "https://lh3.googleusercontent.com/4PYv0EMR96DGOkcPRK7ZTU6IoAYMwYM7M_aPDieJNlZ0HFY-lUKMHwB-DbOUfwBo7EHJuBXFpsTbioA=w544-h544-l90-rj",
        },
        {
            external_id: "2",
            action_label: "Prof Seyi",
            action_location: "https://rivtn.com",
            media_location: "https://lh3.googleusercontent.com/ygYBwJiprrDb8HHVlAdxqBrzoJyl0ngetHFhnzUM-4FrNa5MS9p67GJiUSVmaAHrclWOPBOeRjCy3ki4=w544-h544-l90-rj",
        },
    ];

    static TEST_TEAM_NEWS: Partial<ITeamNews>[] = [
        {
            external_id: "1",
            title: "The new hosting user interface",
            content: "The enabling is done async  messaging channel for all NIgeria related email sending opetrations across the galaxy and through put the universe to the multiver to encompass the infinite omniverse.",
        },
        {
            external_id: "2",
            title: "Setting up your team profile",
            content: "The enabling is done async  messaging channel for all NIgeria related email sending opetrations across the galaxy and through put the universe to the multiver to encompass the infinite omniverse.",
        },
        {
            external_id: "3",
            title: "Yet a new article",
            content: "The enabling is done async  messaging channel for all NIgeria related email sending opetrations across the galaxy and through put the universe to the multiver to encompass the infinite omniverse.",
        },
        {
            external_id: "4",
            title: "The new hosting user interface",
            content: "The enabling is done async  messaging channel for all NIgeria related email sending opetrations across the galaxy and through put the universe to the multiver to encompass the infinite omniverse.",
        },
        {
            external_id: "5",
            title: "Setting up your team profile",
            content: "The enabling is done async  messaging channel for all NIgeria related email sending opetrations across the galaxy and through put the universe to the multiver to encompass the infinite omniverse.",
        },
        {
            external_id: "6",
            title: "Yet a new article",
            content: "The enabling is done async  messaging channel for all NIgeria related email sending opetrations across the galaxy and through put the universe to the multiver to encompass the infinite omniverse.",
        },
        {
            external_id: "7",
            title: "The new hosting user interface",
            content: "The enabling is done async  messaging channel for all NIgeria related email sending opetrations across the galaxy and through put the universe to the multiver to encompass the infinite omniverse.",
        },
        {
            external_id: "8",
            title: "Setting up your team profile",
            content: `
[](https://github.com/exoticlibraries/libcester/raw/main/docs/libcester.png)

# libcester

A robust header only unit testing framework for C programming language. Support function mocking, memory leak detection, crash report. 

cester is a header only automated testing framework for the C programming language, it requires no dependency and can be downloaded and used in a project immediately. Works on various platorms including embedded systems and compatible with various compilers. Allows shared instance \`TestInstance\` object in which each test cases can use to share data and access the command line arguments. 

\`\`\`c\n#include <exotic/cester.h>

CESTER_TEST(test_one, inst,\n    cester_assert_equal(NULL, ((void*)0));    

\`\`\`

The test results can be outputed as various format JunitXML, Test Anything Protocol, Test Anything Protocol Version 13 and text. Visit [https://exoticlibraries.github.io/libcester/docs/](https://exoticlibraries.github.io/libcester/docs/) for documentation and tutorials. 
___

cester is a header only automated testing framework for the C programming language, it requires no dependency and can be downloaded and used in a project immediately. Works on various platorm            
            `,
        },
    ];


}
